import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "../components/FormField";
import { eur } from "../helpers";
import { Plus, Pencil, Trash2, Calculator, FlaskConical, ArrowRight, Save } from "lucide-react";

const LINE_LABELS = { pernottamento: "Pernottamento", pulizie: "Pulizie", extra: "Servizi extra", servizi: "Servizi", spese_anticipate: "Spese anticipate" };
const MODEL_LABELS = { commissione: "Commissione %", canone_fisso: "Canone fisso", revenue_share: "Revenue share", gestione_completa: "Gestione completa" };
const SPLIT_LABELS = { solo_pernottamento: "Solo pernottamento", pernottamento_extra: "Pernottamento + extra", netto_dopo_ota: "Netto dopo OTA", netto_dopo_citytax: "Netto dopo city tax", totale_netto: "Totale netto" };
const OTA_LABELS = { urban: "Urban Dreams", proprietario: "Proprietario", ripartita: "Ripartita", esclusa: "Esclusa dal riparto" };

const emptyInputs = { pernottamento: 800, pulizie: 60, extra: 40, servizi: 0, tassa_soggiorno: 30, commissione_ota: 50, spese_anticipate: 0 };

const defaultContract = () => ({
  name: "", apartment_id: null, owner_id: null, model_type: "commissione",
  vat_lines: { pernottamento: { rate: 10, taxable: true }, pulizie: { rate: 10, taxable: true }, extra: { rate: 10, taxable: true }, servizi: { rate: 10, taxable: true }, spese_anticipate: { rate: 0, taxable: false } },
  prices_vat_included: true, commission_pct: 20, commission_base: "base_riparto",
  revenue_share_pct: 70, fixed_rent_amount: 0, min_guarantee: null,
  split_base: "pernottamento_extra", ota_bearer: "urban", ota_ripartita_urban_pct: 50,
  owner_applies_vat: false, owner_share_vat_rate: 22, owner_vat_regime: "cedolare_secca",
  city_tax_bearer: "urban", notes: "", active: true, version: 1,
});

export default function FinancialEngine() {
  const { t } = useI18n();
  const [tab, setTab] = useState("simulatore");
  const [contracts, setContracts] = useState([]);
  const [apts, setApts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(defaultContract());
  // simulator
  const [simMode, setSimMode] = useState("manuale");
  const [simCfg, setSimCfg] = useState(defaultContract());
  const [simInputs, setSimInputs] = useState(emptyInputs);
  const [bookingId, setBookingId] = useState("");
  const [sim, setSim] = useState(null);

  const load = useCallback(() => { api.get("/economic-contracts").then((r) => setContracts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    load();
    api.get("/apartments/lookup").then((r) => setApts(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data.slice(0, 200))).catch(() => {});
  }, [load]);

  const saveContract = async () => {
    if (!form.name.trim()) { toast.error("Inserisci un nome contratto"); return; }
    try {
      if (form.id) await api.put(`/economic-contracts/${form.id}`, form);
      else await api.post("/economic-contracts", form);
      toast.success("Contratto salvato"); setDlg(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const delContract = async (id) => { if (!window.confirm("Eliminare il contratto?")) return; try { await api.delete(`/economic-contracts/${id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  const runSim = useCallback(async () => {
    try {
      const body = { contract: simCfg };
      if (simMode === "prenotazione" && bookingId) body.booking_id = bookingId;
      else body.inputs = { ...simInputs };
      const r = await api.post("/economic-contracts/simulate", body);
      setSim(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore simulazione"); }
  }, [simCfg, simInputs, simMode, bookingId]);
  useEffect(() => { runSim(); }, [runSim]);

  const setCfg = (k, v) => setSimCfg((s) => ({ ...s, [k]: v }));
  const setVat = (line, key, v) => setSimCfg((s) => ({ ...s, vat_lines: { ...s.vat_lines, [line]: { ...s.vat_lines[line], [key]: v } } }));
  const r = sim?.result; const base = sim?.baseline;

  const Metric = ({ label, value, accent, testid }) => (
    <div className={`rounded-xl border p-3 ${accent || "border-slate-200 bg-white"}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-slate-800">{value != null ? eur(value) : "—"}</div>
    </div>
  );

  return (
    <div className="space-y-6 fade-in" data-testid="financial-engine-page">
      <div className="flex items-center gap-2">
        <button data-testid="tab-simulatore" onClick={() => setTab("simulatore")} className={`rounded-lg px-4 py-2 text-sm font-medium ${tab === "simulatore" ? "bg-primary text-primary-foreground" : "bg-white text-slate-600 border border-slate-200"}`}><FlaskConical size={15} className="mr-1 inline" /> Simulatore</button>
        <button data-testid="tab-contratti" onClick={() => setTab("contratti")} className={`rounded-lg px-4 py-2 text-sm font-medium ${tab === "contratti" ? "bg-primary text-primary-foreground" : "bg-white text-slate-600 border border-slate-200"}`}><Calculator size={15} className="mr-1 inline" /> Contratti economici</button>
      </div>

      {tab === "contratti" && (
        <Card className="border-slate-200 p-4" data-testid="contracts-panel">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-700">Contratti economici</h2>
            <Button size="sm" data-testid="new-contract-btn" onClick={() => { setForm(defaultContract()); setDlg(true); }}><Plus size={15} className="mr-1" /> Nuovo</Button>
          </div>
          <div className="space-y-2">
            {contracts.length === 0 && <div className="text-sm text-slate-400">Nessun contratto. Crea il primo contratto economico configurabile.</div>}
            {contracts.map((c) => (
              <div key={c.id} data-testid={`contract-row-${c.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div>
                  <div className="font-medium text-slate-800">{c.name || "(senza nome)"} <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{MODEL_LABELS[c.model_type]}</span></div>
                  <div className="text-xs text-slate-500">{apts.find((a) => a.id === c.apartment_id)?.name || "Default proprietario"} · comm. {c.commission_pct}% · OTA {OTA_LABELS[c.ota_bearer]}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`edit-contract-${c.id}`} onClick={() => { setForm({ ...defaultContract(), ...c }); setDlg(true); }}><Pencil size={14} /></Button>
                  <Button size="sm" variant="outline" className="text-rose-600" data-testid={`del-contract-${c.id}`} onClick={() => delContract(c.id)}><Trash2 size={14} /></Button>
                  <Button size="sm" variant="outline" className="text-indigo-600" data-testid={`use-contract-${c.id}`} onClick={() => { setSimCfg({ ...defaultContract(), ...c }); setTab("simulatore"); }}>Simula</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "simulatore" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="simulator-panel">
          {/* Left: config + inputs */}
          <div className="space-y-4">
            <Card className="border-slate-200 p-4">
              <div className="mb-3 text-sm font-semibold text-slate-700">Modalità</div>
              <div className="flex gap-2">
                <button data-testid="sim-mode-manuale" onClick={() => setSimMode("manuale")} className={`flex-1 rounded-lg border px-3 py-2 text-sm ${simMode === "manuale" ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600"}`}>Manuale</button>
                <button data-testid="sim-mode-prenotazione" onClick={() => setSimMode("prenotazione")} className={`flex-1 rounded-lg border px-3 py-2 text-sm ${simMode === "prenotazione" ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600"}`}>Da prenotazione reale</button>
              </div>
              {simMode === "prenotazione" ? (
                <div className="mt-3">
                  <Field label="Prenotazione (solo lettura)">
                    <Select value={bookingId} onValueChange={setBookingId}>
                      <SelectTrigger data-testid="sim-booking"><SelectValue placeholder="Seleziona prenotazione" /></SelectTrigger>
                      <SelectContent>{bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.guest_name} · {b.check_in} · {eur(b.total_amount || 0)}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <p className="mt-1 text-xs text-slate-400">La prenotazione reale NON viene modificata. Confronto con il contratto salvato dell'immobile.</p>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {["pernottamento", "pulizie", "extra", "servizi", "tassa_soggiorno", "commissione_ota", "spese_anticipate"].map((k) => (
                    <Field key={k} label={LINE_LABELS[k] || (k === "tassa_soggiorno" ? "Tassa soggiorno" : k === "commissione_ota" ? "Commissione OTA" : k)}>
                      <Input type="number" data-testid={`sim-input-${k}`} value={simInputs[k]} onChange={(e) => setSimInputs((s) => ({ ...s, [k]: Number(e.target.value) }))} />
                    </Field>
                  ))}
                </div>
              )}
            </Card>

            <Card className="border-slate-200 p-4">
              <div className="mb-3 text-sm font-semibold text-slate-700">Parametri contratto (what-if)</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Modello">
                  <Select value={simCfg.model_type} onValueChange={(v) => setCfg("model_type", v)}>
                    <SelectTrigger data-testid="sim-model"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(MODEL_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Base riparto">
                  <Select value={simCfg.split_base} onValueChange={(v) => setCfg("split_base", v)}>
                    <SelectTrigger data-testid="sim-splitbase"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(SPLIT_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                {(simCfg.model_type === "commissione" || simCfg.model_type === "gestione_completa") && <Field label="Commissione %"><Input type="number" data-testid="sim-comm" value={simCfg.commission_pct} onChange={(e) => setCfg("commission_pct", Number(e.target.value))} /></Field>}
                {simCfg.model_type === "revenue_share" && <Field label="Quota proprietario %"><Input type="number" data-testid="sim-rs" value={simCfg.revenue_share_pct} onChange={(e) => setCfg("revenue_share_pct", Number(e.target.value))} /></Field>}
                {simCfg.model_type === "canone_fisso" && <Field label="Canone fisso €"><Input type="number" data-testid="sim-fixed" value={simCfg.fixed_rent_amount} onChange={(e) => setCfg("fixed_rent_amount", Number(e.target.value))} /></Field>}
                <Field label="Commissione OTA a carico">
                  <Select value={simCfg.ota_bearer} onValueChange={(v) => setCfg("ota_bearer", v)}>
                    <SelectTrigger data-testid="sim-ota"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(OTA_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Prezzi IVA inclusa">
                  <div className="flex h-10 items-center"><Switch data-testid="sim-vatincl" checked={simCfg.prices_vat_included} onCheckedChange={(v) => setCfg("prices_vat_included", v)} /></div>
                </Field>
                <div className="col-span-2 flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2">
                  <div className="text-sm text-slate-600">IVA su quota proprietario</div>
                  <div className="flex items-center gap-2">
                    <Switch data-testid="sim-ownervat" checked={simCfg.owner_applies_vat} onCheckedChange={(v) => setCfg("owner_applies_vat", v)} />
                    {simCfg.owner_applies_vat && <Input type="number" className="w-20" data-testid="sim-ownervatrate" value={simCfg.owner_share_vat_rate} onChange={(e) => setCfg("owner_share_vat_rate", Number(e.target.value))} />}
                  </div>
                </div>
              </div>
              {/* Per-line VAT */}
              <div className="mt-3">
                <div className="mb-2 text-xs font-semibold uppercase text-slate-500">IVA per voce</div>
                <div className="space-y-1.5">
                  {["pernottamento", "pulizie", "extra", "servizi", "spese_anticipate"].map((L) => (
                    <div key={L} className="flex items-center gap-3 text-sm">
                      <span className="w-32 text-slate-600">{LINE_LABELS[L]}</span>
                      <Input type="number" className="w-24" data-testid={`sim-vat-${L}`} value={simCfg.vat_lines[L]?.rate ?? 0} onChange={(e) => setVat(L, "rate", Number(e.target.value))} />
                      <span className="text-xs text-slate-400">%</span>
                      <Switch data-testid={`sim-taxable-${L}`} checked={!!simCfg.vat_lines[L]?.taxable} onCheckedChange={(v) => setVat(L, "taxable", v)} />
                      <span className="text-xs text-slate-400">imponibile</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          {/* Right: results */}
          <div className="space-y-4">
            <Card className="border-slate-200 p-4" data-testid="sim-results">
              <div className="mb-3 text-sm font-semibold text-slate-700">Risultato simulazione</div>
              <div className="grid grid-cols-2 gap-2">
                <Metric label="Ricavo Urban Dreams" value={r?.quota_urban_dreams} accent="border-indigo-200 bg-indigo-50" testid="res-urban" />
                <Metric label="Quota Proprietario" value={r?.quota_proprietario} accent="border-emerald-200 bg-emerald-50" testid="res-owner" />
                <Metric label="Commissione OTA" value={r?.commissione_ota} testid="res-ota" />
                <Metric label="IVA 10%" value={r?.iva_10} testid="res-iva" />
                <Metric label="Utile netto Urban" value={r?.utile_netto_urban} accent="border-indigo-200 bg-indigo-50" testid="res-utile" />
                <Metric label="Saldo da bonificare" value={r?.saldo_da_bonificare} accent="border-emerald-200 bg-emerald-50" testid="res-saldo" />
              </div>
              <div className="mt-3 space-y-1 rounded-lg bg-secondary/40 p-3 text-sm">
                {[["Valore totale soggiorno", "valore_totale_soggiorno"], ["Base di riparto", "base_riparto"], ["Imponibile B2C", "imponibile_b2c"], ["IVA quota proprietario", "iva_quota_proprietario"], ["Totale fattura", "totale_fattura"]].map(([lbl, key]) => (
                  <div key={key} className="flex justify-between"><span className="text-slate-500">{lbl}</span><span className="font-medium text-slate-700">{r ? eur(r[key]) : "—"}</span></div>
                ))}
              </div>
              {r?.warnings?.length > 0 && <div className="mt-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700" data-testid="sim-warnings">{r.warnings.join(" · ")}</div>}
              <p className="mt-3 text-[11px] text-slate-400">Anteprima "what-if". Le impostazioni fiscali definitive saranno confermate dal commercialista. La prenotazione reale non viene mai modificata.</p>
            </Card>

            {base && (
              <Card className="border-slate-200 p-4" data-testid="sim-comparison">
                <div className="mb-3 text-sm font-semibold text-slate-700">Confronto: contratto attuale → simulazione</div>
                {[["Quota Proprietario", "quota_proprietario"], ["Ricavo Urban Dreams", "quota_urban_dreams"], ["Saldo da bonificare", "saldo_da_bonificare"]].map(([lbl, key]) => {
                  const delta = (r?.[key] || 0) - (base?.[key] || 0);
                  return (
                    <div key={key} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm last:border-0">
                      <span className="text-slate-500">{lbl}</span>
                      <span className="flex items-center gap-2 text-slate-700">
                        {eur(base[key])} <ArrowRight size={13} className="text-slate-400" /> <b>{eur(r[key])}</b>
                        <span className={`text-xs ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-slate-400"}`}>({delta >= 0 ? "+" : ""}{eur(delta)})</span>
                      </span>
                    </div>
                  );
                })}
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Contract editor dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="contract-dialog">
          <DialogHeader><DialogTitle>{form.id ? "Modifica contratto" : "Nuovo contratto economico"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome" className="col-span-2"><Input data-testid="c-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Immobile">
              <Select value={form.apartment_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, apartment_id: v === "none" ? null : v }))}>
                <SelectTrigger data-testid="c-apartment"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Default proprietario</SelectItem>{apts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Modello">
              <Select value={form.model_type} onValueChange={(v) => setForm((f) => ({ ...f, model_type: v }))}>
                <SelectTrigger data-testid="c-model"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(MODEL_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Commissione %"><Input type="number" data-testid="c-comm" value={form.commission_pct} onChange={(e) => setForm((f) => ({ ...f, commission_pct: Number(e.target.value) }))} /></Field>
            <Field label="Quota proprietario % (rev.share)"><Input type="number" value={form.revenue_share_pct} onChange={(e) => setForm((f) => ({ ...f, revenue_share_pct: Number(e.target.value) }))} /></Field>
            <Field label="Canone fisso €"><Input type="number" value={form.fixed_rent_amount} onChange={(e) => setForm((f) => ({ ...f, fixed_rent_amount: Number(e.target.value) }))} /></Field>
            <Field label="Base riparto">
              <Select value={form.split_base} onValueChange={(v) => setForm((f) => ({ ...f, split_base: v }))}>
                <SelectTrigger data-testid="c-splitbase"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SPLIT_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Commissione OTA a carico">
              <Select value={form.ota_bearer} onValueChange={(v) => setForm((f) => ({ ...f, ota_bearer: v }))}>
                <SelectTrigger data-testid="c-ota"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(OTA_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annulla</Button>
            <Button data-testid="save-contract-btn" onClick={saveContract}><Save size={15} className="mr-1" /> Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
